/**
 * NodeShapeRoot — keyboard-accessible SVG node wrapper.
 *
 * Renders the type-specific shape via `ShapeRenderer`, an upper-right
 * status dot per design spec, and the focusable hit area. `Tab` reaches
 * the node; `Enter`/`Space` invokes `onActivate` (sprint-3 wires this
 * to the Investigation Node deep-link bridge).
 *
 * cycle-102 sprint-2 Task 2.3 — sprint plan §307 + §351.
 */

import { useCallback, type KeyboardEvent } from 'react';

import {
  NODE_TYPE_TO_COLOUR_VAR,
  NODE_TYPE_TO_SHAPE,
  STATUS_DOT_VAR,
  STATUS_FILL_OPACITY,
  type NodeStatus,
  type NodeType,
} from './nodeMaps';
import { ShapeRenderer } from './ShapeRenderer';

export interface NodeShapeProps {
  id: string;
  /** Node-graph type — drives shape + colour from the design tokens. */
  type: NodeType;
  /** Current status — drives fill colour and opacity. */
  status: NodeStatus;
  /** Centre X in SVG coordinates. */
  cx: number;
  /** Centre Y in SVG coordinates. */
  cy: number;
  /** Nominal (pre-AREA_SCALE) radius. */
  radius: number;
  /** Optional human-readable label for screen readers. */
  label?: string;
  /** Whether this node is the current selection — drives focus ring. */
  selected?: boolean;
  /** Callback when the operator presses Enter or Space on the node. */
  onActivate?: (id: string) => void;
  /** Callback when the operator clicks the node. */
  onSelect?: (id: string) => void;
}

const STATUS_DOT_RADIUS = 3;

export function NodeShapeRoot({
  id,
  type,
  status,
  cx,
  cy,
  radius,
  label,
  selected = false,
  onActivate,
  onSelect,
}: NodeShapeProps) {
  const shape = NODE_TYPE_TO_SHAPE[type];
  const stroke = NODE_TYPE_TO_COLOUR_VAR[type];
  const fill = STATUS_DOT_VAR[status];
  const fillOpacity = STATUS_FILL_OPACITY[status];

  const handleKey = useCallback(
    (e: KeyboardEvent<SVGGElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate?.(id);
      }
    },
    [id, onActivate],
  );

  // Status dot offset — upper-right corner per design spec.
  const dotOffset = radius * 0.85;

  const accessibleLabel =
    label ?? `${type} node ${id} — ${status}`;

  return (
    <g
      role="button"
      aria-label={accessibleLabel}
      aria-pressed={selected}
      tabIndex={0}
      data-testid={`node-${id}`}
      data-node-type={type}
      data-node-status={status}
      data-node-shape={shape}
      onKeyDown={handleKey}
      onClick={() => onSelect?.(id)}
      style={{ cursor: 'pointer', outline: 'none' }}
    >
      {selected && (
        <circle
          cx={cx}
          cy={cy}
          r={radius + 4}
          fill="none"
          stroke={stroke}
          strokeOpacity={0.6}
          strokeWidth={1}
          data-testid={`node-${id}-focus-ring`}
        />
      )}
      <ShapeRenderer
        shape={shape}
        cx={cx}
        cy={cy}
        radius={radius}
        fill={fill}
        fillOpacity={fillOpacity}
        stroke={stroke}
        strokeOpacity={0.9}
        strokeWidth={1}
        data-testid={`node-${id}-shape`}
      />
      <circle
        cx={cx + dotOffset}
        cy={cy - dotOffset}
        r={STATUS_DOT_RADIUS}
        fill={STATUS_DOT_VAR[status]}
        stroke="var(--bg-card)"
        strokeWidth={1}
        data-testid={`node-${id}-status-dot`}
        aria-hidden="true"
      />
    </g>
  );
}
